import { getCriteriaForRole, getEnrichedCriteria } from '../data/scorecardCriteria';

const ANCHOR_COLS = [
  { key: 'no',        label: 'No',         bg: '#FADBDB', text: '#C0392B' },
  { key: 'maybe',     label: 'Maybe',      bg: '#FDEBD0', text: '#E67E22' },
  { key: 'yes',       label: 'Yes',        bg: '#D5F5E3', text: '#1A7A6E' },
  { key: 'strongYes', label: 'Strong yes', bg: '#D6EAF8', text: '#1B4F72' },
];

/**
 * ScorecardCard — interactive candidate evaluation scorecard.
 * Props:
 *   role (string) — used to look up role-calibrated criteria from scorecardCriteria.js
 *   isUnlocked (bool) — whether the user has paid; shows locked state if false
 *
 * Criteria are always sourced from scorecardCriteria.js (getCriteriaForRole → DEFAULT_CRITERIA).
 * AI-generated criteria are never used.
 */
export default function ScorecardCard({ role, isUnlocked = false, apiScorecardCriteria = null }) {
  const criteria = getCriteriaForRole(role);
  const enrichedCriteria = getEnrichedCriteria(role, apiScorecardCriteria);

  const cardStyle = {
    background: '#fff',
    border: '1px solid #E5E5E5',
    borderRadius: '10px',
    padding: '20px',
    marginTop: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  };

  const headingStyle = {
    fontFamily: 'Inter, sans-serif',
    fontSize: '16px',
    fontWeight: 700,
    borderBottom: '2px solid #1A7A6E',
    paddingBottom: '6px',
    marginBottom: '12px',
    color: '#1A1A1A',
  };

  if (!isUnlocked) {
    return (
      <div style={cardStyle}>
        <h3 style={headingStyle}>Candidate Scorecard</h3>
        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#555555', marginBottom: '14px', lineHeight: 1.5 }}>
          Rate every candidate on the same criteria, so your decision comes down to evidence rather than gut feel.
        </p>
        <div style={{ marginBottom: '12px' }}>
          <div style={{ background: '#EBF5FB', borderLeft: '2px solid #1B4F72', padding: '8px 12px', fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 700, color: '#1B4F72', marginBottom: '4px', borderRadius: '0 4px 4px 0' }}>
            {criteria[0] || 'Relevant experience and skills'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
            {[
              { label: 'No (1)', bg: '#FADBDB', color: '#C0392B', text: 'Cannot demonstrate relevant experience for this role.' },
              { label: 'Maybe (2)', bg: '#FDEBD0', color: '#E67E22', text: 'Some background but gaps that would need close support.' },
              { label: 'Yes (3)', bg: '#D5F5E3', color: '#1A7A6E', text: 'Clear relevant experience, can talk through real examples.' },
              { label: 'Strong Yes (4)', bg: '#D6EAF8', color: '#1B4F72', text: 'Standout experience directly matched to what this role needs.' },
            ].map((cell, i) => (
              <div key={i} style={{ background: cell.bg, padding: '8px 10px', borderRadius: 3 }}>
                <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', fontWeight: 700, color: cell.color, marginBottom: 3 }}>{cell.label}</div>
                <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: '#444444', lineHeight: 1.4 }}>{cell.text}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ padding: '10px 12px', background: '#F5F5F5', borderRadius: '6px', fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#555555' }}>
          🔒 {criteria.length - 1} more role-calibrated criteria with anchor descriptions — unlock to rate candidates
        </div>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <h3 style={headingStyle}>Candidate Scorecard</h3>
      <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#555555', marginBottom: '16px', lineHeight: 1.5 }}>
        Use during or after each interview — rate every candidate on the same criteria, then compare scorecards to make your decision.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {enrichedCriteria.map((item, i) => (
          <div key={i}>
            <div style={{ background: '#EBF5FB', borderLeft: '2px solid #1B4F72', padding: '8px 12px', fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 700, color: '#1B4F72', marginBottom: '4px', borderRadius: '0 4px 4px 0' }}>
              {item.label}
            </div>
            {item.anchors ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                {ANCHOR_COLS.map(col => (
                  <div key={col.key} style={{ background: col.bg, padding: '8px 10px', borderRadius: 3 }}>
                    <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', fontWeight: 700, color: col.text, marginBottom: 3 }}>{col.label}</div>
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                      {(item.anchors[col.key] || []).map((bullet, bi) => (
                        <li key={bi} style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: '#374151', lineHeight: 1.5, marginBottom: '2px' }}>• {bullet}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>

      <button
        style={{
          marginTop: '14px',
          padding: '10px 14px',
          borderRadius: '6px',
          border: '1px solid #CCCCCC',
          background: '#fff',
          cursor: 'pointer',
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px',
          width: '100%',
        }}
        onClick={() => {
          const lines = [
            `Candidate Scorecard — ${role}`,
            'Rate each candidate: No (1) / Maybe (2) / Yes (3) / Strong yes (4)',
            '',
            ...enrichedCriteria.flatMap(item => {
              if (!item.anchors) return [item.label, ''];
              return [
                item.label,
                `  No: ${(item.anchors.no || []).join(' ')}`,
                `  Maybe: ${(item.anchors.maybe || []).join(' ')}`,
                `  Yes: ${(item.anchors.yes || []).join(' ')}`,
                `  Strong yes: ${(item.anchors.strongYes || []).join(' ')}`,
                '',
              ];
            }),
          ];
          navigator.clipboard.writeText(lines.join('\n'));
        }}
      >
        Copy scorecard
      </button>
    </div>
  );
}
